/**
 * Results of a TEF Canada sitting (D-109): a score out of 699 and the NCLC of each ability (the
 * official table of Le français des affaires), the CEFR level, the right answers by section, the
 * written and oral expression marked on the TEF grid (sections A and B), then the correction of
 * every question, document by document: the audio heard again without limit with its transcript,
 * the answer given and the right one, the explanation (and its English help), and « How to
 * succeed in this type of question? ». Always an estimate, never an official result.
 */

import { MarkNotes } from "../MarkNotes";
import { RecordingExpired } from "../RecordingExpired";
import { ApiError, useAcademyAttemptsRetrieve, useAcademyAttemptsReviewRetrieve } from "@agencyos/api-client";
import {
  type ExamPicture,
  type IeltsProduction,
  type IeltsReviewQuestion,
  type SpokenLine,
  type TefResults as Results,
  answerText,
  formatScore,
  hasPictures,
  lowestNclc,
  oldScoreText,
  parseIeltsReview,
  parsePicture,
  parseSpokenLines,
  parseTefResults,
  pick,
  reviewHelp,
  reviewTables,
} from "@agencyos/core";
import { Badge, GlassCard, Skeleton, buttonClass } from "@agencyos/ui-web";
import { ArrowLeft, CheckCircle2, Compass, Hourglass, Lightbulb, Route, Sprout, XCircle } from "lucide-react";
import { type CSSProperties, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useIeltsFormat } from "../ielts/CoursePage";
import { MethodLink } from "../ielts/MethodLink";
import { ExamPictureView, PictureOptions } from "../ExamPicture";
import { TableCharts } from "../TableCharts";
import { AudioReplay, ListeningReplay } from "../ListeningReplay";
import { SKILLS, type Skill, isObject, isSkill } from "../types";
import { CardTitle, PageHead, Ring, SectionTitle, SkillIcon } from "../ui";
import { AiUnavailable } from "../AiUnavailable";
import { unavailableSkills, useAcademyPaths } from "../paths";
import { FeedbackInMyLanguage, InMyLanguage } from "../ielts/InMyLanguage";

const TEF = "tef_canada";

function waiting(results: Results): "all" | "some" | "none" {
  const shown = SKILLS.filter((s) => results.skills[s]);
  if (!shown.length && !results.placement) return "all";
  return shown.some((s) => results.skills[s]?.pending) ? "some" : "none";
}

function ScoreCards({ results, unavailable }: { results: Results; unavailable?: Set<string> }) {
  const { t } = useTranslation();
  const shown = SKILLS.filter((s) => results.skills[s]);
  return (
    <ul className="ac-scores">
      {shown.map((skill, index) => {
        const row = results.skills[skill];
        return (
          <li key={skill} className={`ac-score ac-skill--${skill} ao-enter`} style={{ "--ao-i": index } as CSSProperties}>
            <div className="ac-score__head">
              <SkillIcon skill={skill} size={16} />
              <h2 className="ac-headline">{t(`tef.skill.${skill}`)}</h2>
            </div>
            {unavailable?.has(skill) ? (
              <AiUnavailable />
            ) : !row || row.pending || row.score === null ? (
              <p className="ac-status ac-secondary ac-score__pending">
                <Hourglass aria-hidden size={18} />
                {t("academy.results.grading")}
              </p>
            ) : (
              <>
                <div role="meter" aria-label={t("tef.results.scoreOf", { score: row.score })} aria-valuemin={0} aria-valuemax={699} aria-valuenow={row.score}>
                  <Ring ratio={row.score / 699} size={132} stroke={11} tone={skill}>
                    <span className="ac-score__value ao-num">{row.score}</span>
                    <span className="ac-score__max">/ 699</span>
                  </Ring>
                </div>
                <p className="ac-score__levels">
                  {row.nclc !== null && <span className="ac-score__nclc">{row.nclc > 0 ? t("tef.nclc", { n: row.nclc }) : t("tef.belowNclc4")}</span>}
                  {row.cefr && <Badge>{t("academy.results.cefr", { level: row.cefr })}</Badge>}
                </p>
                {row.right !== null && row.questions !== null && <p className="ac-caption ao-num">{t("tef.results.right", { right: row.right, total: row.questions })}</p>}
                {row.oldScore && oldScoreText(row.oldScore) && (
                  <p className="ac-caption ao-num">{t("conformity.tef.oldScore", { range: oldScoreText(row.oldScore) })}</p>
                )}
                {row.unanswered > 0 && (
                  <p className="ac-caption ao-num ko">{t("academy.results.unansweredCount", { count: row.unanswered, total: row.tasksPlanned })}</p>
                )}
                {(skill === "ee" || skill === "eo") && Object.keys(row.sections).length > 0 && (
                  <p className="ac-caption ao-num">
                    {Object.entries(row.sections)
                      .map(([section, value]) => t("tef.results.sectionScore", { section, score: typeof value === "number" ? value : 0 }))
                      .join(" · ")}
                  </p>
                )}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** D-163: what IRCC asks for in the Express Entry profile (canada.ca, language testing). */
function OldScoreNote({ results }: { results: Results }) {
  const { t } = useTranslation();
  if (!SKILLS.some((s) => results.skills[s]?.oldScore)) return null;
  return (
    <p className="ac-note ac-note--warning" role="note">
      {t("conformity.tef.irccWarning")}{" "}
      <a className="ac-link" href="https://www.canada.ca/en/immigration-refugees-citizenship/services/immigrate-canada/express-entry/documents/language-requirements/language-testing.html" target="_blank" rel="noreferrer">
        {t("conformity.tef.irccLink")}
      </a>
    </p>
  );
}

function LowestCard({ results }: { results: Results }) {
  const { t } = useTranslation();
  const lowest = results.nclcMin ?? lowestNclc(results);
  if (lowest === null) return null;
  return (
    <GlassCard as="section" className="ac-card ac-overall" aria-labelledby="tef-lowest">
      <Ring ratio={Math.min(lowest, 12) / 12} size={160} stroke={13} tone="accent">
        <span className="ac-overall__value ao-num">{lowest}</span>
        <span className="ac-caption">NCLC</span>
      </Ring>
      <div className="ac-overall__text">
        <p className="ac-eyebrow">{t("tef.results.estimateEyebrow")}</p>
        <h2 id="tef-lowest" className="ac-title-2">
          {t("tef.results.lowest", { n: lowest })}
        </h2>
        <p className="ac-footnote">{t("tef.results.lowestNote")}</p>
      </div>
    </GlassCard>
  );
}

function QuestionReview({ q, help, type }: { q: IeltsReviewQuestion; help: string; type: string }) {
  const { t } = useTranslation();
  const [english, setEnglish] = useState(false);
  const mine = q.response === null || q.response === -1 ? "" : answerText(q, q.response);
  return (
    <li className={`ac-review ac-surface${q.correct ? " ac-review--right" : " ac-review--wrong"}`}>
      <p className="ac-review__head">
        <span className="ac-review__mark" aria-hidden="true">
          {q.correct ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
        </span>
        <strong className="ac-caption ao-num">{t("ielts.player.questionN", { n: q.number ?? "" })}</strong>
        <span className="visually-hidden">{q.correct ? t("academy.player.right") : t("academy.results.wrongShort")}</span>
      </p>
      <ExamPictureView picture={q.image} lang="fr" size="medium" />
      <p className="ac-review__stem" lang="fr">
        {q.prompt}
      </p>
      {hasPictures(q.optionImages) && (
        <PictureOptions
          name={`tr-${q.id}`}
          options={q.options}
          images={q.optionImages}
          answer={typeof q.accepted === "number" ? q.accepted : null}
          given={typeof q.response === "number" ? q.response : null}
          showText
          lang="fr"
        />
      )}
      <dl className="ac-review__answers">
        <div>
          <dt>{t("ielts.results.yours")}</dt>
          <dd lang="fr">{mine || t("academy.results.noAnswer")}</dd>
        </div>
        <div>
          <dt>{t("ielts.results.expected")}</dt>
          <dd lang="fr">{answerText(q, q.accepted)}</dd>
        </div>
      </dl>
      {q.explanation ? (
        <>
          <p className="ac-review__why" lang={english ? "en" : "fr"}>
            {english && help ? help : q.explanation}
          </p>
          <InMyLanguage text={q.explanation} source="fr" examLanguage="fr" kind="correction" />
        </>
      ) : (
        <p className="ac-review__why ac-review__why--pending">{t("listening.review.explanationSoon")}</p>
      )}
      {help && (
        <button type="button" className="link ac-footnote" aria-pressed={english} onClick={() => setEnglish((v) => !v)}>
          {english ? t("tef.results.showFrench") : t("tef.results.showEnglish")}
        </button>
      )}
      {!q.correct && q.skill && <MethodLink exam={TEF} skill={q.skill} type={type} />}
    </li>
  );
}

function ProductionReview({ production, score, labelOf }: { production: IeltsProduction; score: number | null; labelOf: (skill: string, key: string) => string }) {
  const { t } = useTranslation();
  const { grade } = production;
  const skill: Skill = isSkill(production.skill) ? production.skill : "ee";
  const letter = production.task === 2 ? "B" : "A";
  return (
    <article className="ac-production">
      <h3 className="ac-title-3">
        {t(`tef.skill.${skill}`)} · {t("tef.results.section", { section: letter })}
        {score !== null && ` · ${formatScore(score)}`}
      </h3>
      <p className="ac-prompt ac-prompt--small" lang="fr">
        {production.prompt}
      </p>
      {production.text && (
        <details>
          <summary>{skill === "eo" ? t("academy.results.transcript") : t("academy.results.yourText")}</summary>
          <p className="ac-production-text" lang="fr">
            {production.text}
          </p>
          {skill === "ee" && <p className="ac-footnote">{t("academy.writing.wordsPlain", { count: production.wordCount })}</p>}
        </details>
      )}
      {skill === "eo" && production.turns.length > 0 && (
        <details>
          <summary>{t("tef.results.conversation")}</summary>
          <ul className="ac-transcript">
            {production.turns.map((turn, index) => (
              <li key={index} className={`ac-bubble ac-bubble--${turn.role === "examiner" ? "them" : "me"}`}>
                <strong className="ac-bubble__who">{t(`tef.speaking.who.${turn.role === "examiner" ? "examiner" : "learner"}`)}</strong> <span lang="fr">{turn.text}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {skill === "eo" && production.audioUrl && (
        <div className="ac-listen-again">
          <p className="ac-label">{t("academy.results.listenAgain")}</p>
          <AudioReplay url={production.audioUrl} label={t("academy.results.listenAgain")} />
        </div>
      )}
      {production.audioExpired && <RecordingExpired />}
      {production.unanswered && <p className="ac-status ko">{t("academy.results.unanswered")}</p>}
      <MarkNotes grade={production.rawGrade} productionId={production.id} />
      {(production.status === "to_trainer" || production.status === "submitted") && (
        <p className="ac-status ac-secondary">
          <Hourglass aria-hidden size={18} />
          {production.status === "to_trainer" ? t("academy.results.toTrainer") : t("academy.results.grading")}
        </p>
      )}
      {grade.criteria.length > 0 && (
        <table className="ac-criteria">
          <caption className="visually-hidden">{t("academy.results.criteria")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("academy.results.criterion")}</th>
              <th scope="col" className="num">
                {t("tef.results.points")}
              </th>
            </tr>
          </thead>
          <tbody>
            {grade.criteria.map((c) => (
              <tr key={c.key}>
                <td>
                  <strong>{labelOf(skill, c.key)}</strong>
                  {c.comment && <p className="ac-footnote">{c.comment}</p>}
                </td>
                <td className="num ao-num">{c.band ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {grade.comment && <p className="ac-comment">{grade.comment}</p>}
      <FeedbackInMyLanguage comment={grade.comment} errors={grade.errors} examLanguage="fr" />
      {grade.errors.length > 0 && (
        <div className="ac-block">
          <h4 className="ac-label">{t("academy.results.errors")}</h4>
          <ul className="ac-errors">
            {grade.errors.map((e, index) => (
              <li key={index} className="ac-error-row">
                <p lang="fr">
                  <del>{e.excerpt}</del> → <ins>{e.correction}</ins>
                </p>
                {e.explanation && <p className="ac-footnote">{e.explanation}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {grade.corrected && (
        <details>
          <summary>{t("academy.results.corrected")}</summary>
          <p className="ac-production-text" lang="fr">
            {grade.corrected}
          </p>
        </details>
      )}
      {grade.advice.length > 0 && (
        <div className="ac-block ac-advice">
          <h4 className="ac-label">
            <Lightbulb aria-hidden size={14} /> {t("academy.results.advice")}
          </h4>
          <ul>
            {grade.advice.map((a, index) => (
              <li key={index}>{a}</li>
            ))}
          </ul>
        </div>
      )}
      <MethodLink exam={TEF} skill={skill} task={production.task} />
    </article>
  );
}

interface Doc {
  section: number;
  doc: number;
  context: string;
  audioUrl: string | null;
  transcript: SpokenLine[];
  image: ExamPicture | null;
}

/** The CO documents of the review, in order: audio to hear again and transcript. */
function documents(value: unknown): Doc[] {
  const rows = isObject(value) && Array.isArray(value.listening) ? value.listening : [];
  return rows.filter(isObject).map((row) => ({
    section: typeof row.part === "number" ? row.part : 0,
    doc: typeof row.doc === "number" ? row.doc : 0,
    context: typeof row.context === "string" ? row.context : "",
    audioUrl: typeof row.audio_url === "string" && row.audio_url ? row.audio_url : null,
    transcript: parseSpokenLines(row.transcript),
    image: parsePicture(row.image),
  }));
}

/** The score out of 699 of each production of the review (sections A and B), in order. */
function productionScores(value: unknown): (number | null)[] {
  const rows = isObject(value) && Array.isArray(value.productions) ? value.productions : [];
  return rows.map((row) => (isObject(row) && typeof row.score === "number" ? row.score : null));
}

/** The passage (document) of each review question, in the order of the review. */
function passageOf(value: unknown): string[] {
  const rows = isObject(value) && Array.isArray(value.questions) ? value.questions : [];
  return rows.map((row) => (isObject(row) && typeof row.passage === "string" ? row.passage : ""));
}

export function TefResults({ id }: { id: string }) {
  const paths = useAcademyPaths();
  const { t, i18n } = useTranslation();
  const attempt = useAcademyAttemptsRetrieve(id, { query: { retry: false } });
  const raw: unknown = attempt.data?.status === 200 ? attempt.data.data : undefined;
  const status = isObject(raw) ? String(raw.status ?? "") : "";
  const stateResults = isObject(raw) ? parseTefResults(raw.results) : null;
  const inProgress = status === "in_progress";
  const review = useAcademyAttemptsReviewRetrieve(id, {
    query: {
      enabled: Boolean(raw) && !inProgress,
      retry: false,
      refetchInterval: (query) => {
        const data = query.state.data?.status === 200 ? parseTefResults((query.state.data.data as { results?: unknown }).results) : null;
        const wait = data ? waiting(data) : "all";
        return wait === "all" ? 4000 : wait === "some" ? 20000 : false;
      },
    },
  });
  const format = useIeltsFormat(TEF);
  const reviewData: unknown = review.data?.status === 200 ? review.data.data : null;
  const data = useMemo(() => (reviewData ? parseIeltsReview(reviewData) : null), [reviewData]);
  const help = useMemo(() => reviewHelp(reviewData), [reviewData]);
  const passages = useMemo(() => passageOf(reviewData), [reviewData]);
  const docs = useMemo(() => documents(reviewData), [reviewData]);
  const tables = useMemo(() => reviewTables(reviewData), [reviewData]);
  const scores = useMemo(() => productionScores(reviewData), [reviewData]);
  const labelOf = (skill: string, key: string) => {
    const row = (format.rubrics[skill] ?? []).find((r) => r.key === key);
    return row ? pick(row.label, i18n.language) : key;
  };

  const back = (
    <Link to={paths.home} className="ac-back">
      <ArrowLeft aria-hidden size={16} />
      {t("academy.results.back")}
    </Link>
  );
  if (attempt.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (attempt.error instanceof ApiError && attempt.error.status === 404) {
    return (
      <div className="ac-page">
        {back}
        <p className="ac-secondary">{t("academy.results.notFound")}</p>
      </div>
    );
  }
  if (inProgress) {
    return (
      <div className="ac-surface ac-card ac-card--center">
        <p>{t("academy.results.notFinished")}</p>
        <Link className={buttonClass()} to={paths.attempt(id)}>
          {t("academy.history.resume")}
        </Link>
      </div>
    );
  }

  const results = (reviewData && isObject(reviewData) ? parseTefResults(reviewData.results) : null) ?? stateResults ?? { skills: {}, nclcMin: null, placement: null };
  const placement = results.placement;
  if (placement) {
    return (
      <div className="ac-page ac-results">
        {back}
        <PageHead eyebrow={t("tef.placement.title")} title={t("tef.placement.resultTitle")} subtitle={<p className="ac-note">{t("tef.estimateLong")}</p>} />
        <GlassCard as="section" className={`ac-card ac-overall${placement.beginner ? " ac-card--success" : ""}`} aria-labelledby="tef-placement">
          <Ring ratio={Math.min(placement.nclc ?? 0, 12) / 12} size={160} stroke={13} tone="accent">
            <span className="ac-overall__value ao-num">{placement.nclc ?? "—"}</span>
            <span className="ac-caption">NCLC</span>
          </Ring>
          <div className="ac-overall__text">
            <CardTitle id="tef-placement" icon={placement.beginner ? <Sprout size={16} /> : <Compass size={16} />} tone={placement.beginner ? "success" : "info"}>
              {placement.beginner ? t("tef.placement.beginnerTitle") : t("tef.placement.levelTitle", { level: t(`ielts.levels.${placement.level}`, { defaultValue: placement.level }) })}
            </CardTitle>
            <p className="ac-secondary">{placement.beginner ? t("tef.placement.beginnerText") : t("tef.placement.levelText", { cefr: placement.cefr, nclc: placement.nclc ?? 0 })}</p>
            <div className="ac-card__actions ac-card__actions--start">
              <Link className={buttonClass("primary")} to="/academy/course">
                <Route aria-hidden size={16} />
                {t("tef.placement.toCourse")}
              </Link>
            </div>
          </div>
        </GlassCard>
      </div>
    );
  }

  const wait = waiting(results);
  const questions = data?.questions ?? [];
  const productions = data?.productions ?? [];
  const indexed = questions.map((q, index) => ({ q, passage: passages[index] ?? "" }));
  const groups = (skill: Skill) => {
    const out: { passage: string; items: IeltsReviewQuestion[] }[] = [];
    for (const { q, passage } of indexed.filter((row) => row.q.skill === skill)) {
      const last = out[out.length - 1];
      if (last && last.passage === passage) last.items.push(q);
      else out.push({ passage, items: [q] });
    }
    return out;
  };

  return (
    <div className="ac-page ac-results">
      {back}
      <PageHead eyebrow={t("tef.results.eyebrow")} title={t("tef.results.title")} subtitle={<p className="ac-note" role="note">{t("tef.estimateLong")}</p>} />
      {(review.isLoading || wait === "all") && (
        <div className="ac-surface ac-card ac-card--center ac-preparing">
          <p className="ac-status" role="status">
            {t("academy.results.preparing")}
          </p>
        </div>
      )}
      {wait !== "all" && <LowestCard results={results} />}
      {wait !== "all" && <ScoreCards results={results} unavailable={unavailableSkills(raw)} />}
      {wait !== "all" && <OldScoreNote results={results} />}

      {productions.length > 0 && (
        <section className="ac-section" aria-labelledby="tef-productions">
          <SectionTitle id="tef-productions">{t("tef.results.productions")}</SectionTitle>
          <div className="ac-stack-cards">
            {productions.map((p, index) => (
              <GlassCard key={`${p.skill}-${p.task}`} as="div" className="ac-card">
                <ProductionReview production={p} score={scores[index] ?? null} labelOf={labelOf} />
              </GlassCard>
            ))}
          </div>
        </section>
      )}

      {(["co", "ce"] as Skill[]).map((skill) => {
        const list = groups(skill);
        if (!list.length) return null;
        let coIndex = 0;
        return (
          <section key={skill} className="ac-section" aria-labelledby={`tef-review-${skill}`}>
            <SectionTitle id={`tef-review-${skill}`}>{t("tef.results.review", { skill: t(`tef.skill.${skill}`) })}</SectionTitle>
            {list.map((group, index) => {
              const doc = skill === "co" ? docs[coIndex++] : undefined;
              const first = group.items[0];
              const type = first ? (help[first.id]?.type ?? "") : "";
              return (
                <div key={`${group.passage}-${index}`} className="ac-replay-part">
                  <h3 className="ac-headline">
                    {t("tef.player.documentN", { n: index + 1 })}
                    {type ? ` · ${t(`tef.doc.${type}`, { defaultValue: type })}` : ""}
                  </h3>
                  {doc && <ExamPictureView picture={doc.image} lang="fr" size="medium" />}
                  {doc && <ListeningReplay audioUrl={doc.audioUrl} lines={doc.transcript} lang="fr" preferred="fr-FR" context={doc.context} />}
                  {skill === "ce" && <TableCharts table={tables[group.passage] ?? null} lang="fr" />}
                  <ol className="ac-review-list">
                    {group.items.map((q) => (
                      <QuestionReview key={q.id} q={q} help={help[q.id]?.en ?? ""} type={help[q.id]?.type ?? ""} />
                    ))}
                  </ol>
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
